import React, { useEffect, useRef, useState } from 'react';
import {
  AreaChart, Donut, Heatmap, StatCard, DataTable,
  COLORS, prefersReducedMotion,
} from './charts';
import './ModuleStages.css';

// Full-width console stages for the public module pages. Fixture data only.
// The last frame is what prefers-reduced-motion holds.

function useStageTick(ms, length, paused) {
  const [i, setI] = useState(() => (prefersReducedMotion() ? length - 1 : 0));
  useEffect(() => {
    if (prefersReducedMotion() || paused) return undefined;
    const t = setInterval(() => setI((v) => (v + 1) % length), ms);
    return () => clearInterval(t);
  }, [ms, length, paused]);
  return i;
}

function nodeState(stage, tick) {
  if (tick > stage) return 'done';
  if (tick === stage) return 'running';
  return 'queued';
}

/* The seeded "Product launch — swarm of swarms" example
   (scripts/seed-example-pipelines.mjs ex-launch-swarm): a coordinator plans,
   "Call pipeline … each" hands every workstream to a callee that runs its
   OWN coordinator and refine loop, a keyword branch detours HIGH risk through
   a human, both branches converge, one sign-off gates two writes. Node
   geometry: 152px wide, ports at mid-height (36px). */
const NQ_W = 140; const NQ_PITCH = 170; const NQ_PY = 36;
const nx = (c) => 8 + c * NQ_PITCH;
const NQ_NODES = [
  { id: 'plan', title: 'Planning swarm', type: 'Coordinator', color: '#14e0c8', x: nx(0), y: 150, stage: 0, badge: (t) => (t === 0 ? 'R1 · 4 roles' : 'R2 · 6 roles · approved') },
  { id: 'each', title: 'Call per stream', type: 'Call pipeline · each', color: '#8b7cff', x: nx(1), y: 150, stage: 1, badge: (t) => (t === 1 ? 'call 3 / 4 · depth 1' : '4 calls · depth 1') },
  { id: 'risky', title: 'Any HIGH risk?', type: 'Branch filter · keyword', color: '#ffd166', x: nx(2), y: 150, stage: 2, badge: (t) => (t > 2 ? 'pass · 1 of 4 HIGH' : null) },
  { id: 'review', title: 'Lead triages risk', type: 'Human review · urgent', color: '#ffd166', x: nx(3), y: 20, stage: 3, badge: (t) => (t > 3 ? 'approved · mitigation edited' : null) },
  { id: 'merge', title: 'Assemble + refine', type: 'LLM → Refine loop', color: '#8b7cff', x: nx(3), y: 150, stage: 4, badge: (t) => (t === 4 ? 'refine round 2 / 2' : t > 4 ? 'approved in 2 rounds' : null) },
  { id: 'signoff', title: 'Exec sign-off', type: 'Human review', color: '#ffd166', x: nx(4), y: 150, stage: 5, badge: (t) => (t > 5 ? 'approved' : null) },
  { id: 'pub', title: 'Publish plan', type: 'Knowledge · capability', color: '#37d99a', x: nx(5), y: 80, stage: 6 },
  { id: 'announce', title: 'Announce', type: 'Webhook · capability', color: '#37d99a', x: nx(5), y: 220, stage: 6 },
];
// Callee inset: what runs inside "Swarm per workstream", once per item.
const NQ_SUB = [
  { id: 's-swarm', title: 'Stream swarm', type: 'Coordinator', color: '#14e0c8' },
  { id: 's-refine', title: 'Shape brief', type: 'Refine loop · 2 rounds', color: '#8b7cff' },
  { id: 's-grade', title: 'Grade risk', type: 'LLM · RISK: HIGH|LOW', color: '#8b7cff' },
];
const port = (n, side) => ({ x: side === 'out' ? n.x + NQ_W : n.x, y: n.y + NQ_PY });
const curve = (a, b) => `M${a.x} ${a.y} C ${a.x + 24} ${a.y}, ${b.x - 24} ${b.y}, ${b.x} ${b.y}`;
const byId = Object.fromEntries(NQ_NODES.map((n) => [n.id, n]));
const lp = (n, dx, dy) => ({ x: n.x + NQ_W + dx, y: n.y + NQ_PY + dy });
const NQ_EDGES = [
  { id: 'plan-each', d: curve(port(byId.plan, 'out'), port(byId.each, 'in')), stage: 1 },
  { id: 'each-risky', d: curve(port(byId.each, 'out'), port(byId.risky, 'in')), stage: 2 },
  { id: 'risky-review', d: curve(port(byId.risky, 'out'), port(byId.review, 'in')), stage: 3, label: 'pass', lp: lp(byId.risky, 2, -52) },
  { id: 'risky-merge', d: curve(port(byId.risky, 'out'), port(byId.merge, 'in')), stage: 4, label: 'fail', dim: true, lp: lp(byId.risky, 2, 6) },
  { id: 'review-merge', d: `M${byId.review.x + NQ_W / 2} ${byId.review.y + 72} L ${byId.merge.x + NQ_W / 2} ${byId.merge.y}`, stage: 4, label: 'approve', lp: { x: byId.review.x + NQ_W / 2 + 6, y: byId.review.y + 96 } },
  { id: 'merge-signoff', d: curve(port(byId.merge, 'out'), port(byId.signoff, 'in')), stage: 5 },
  { id: 'signoff-pub', d: curve(port(byId.signoff, 'out'), port(byId.pub, 'in')), stage: 6, label: 'approve', lp: lp(byId.signoff, 2, -40) },
  { id: 'signoff-announce', d: curve(port(byId.signoff, 'out'), port(byId.announce, 'in')), stage: 6 },
];
const SUB_X0 = nx(0) + 8; const SUB_Y = 318; const SUB_PITCH = 156;
const NQ_INSPECT = {
  plan: { title: 'Launch planning swarm', rows: [['Block', 'Coordinator'], ['Roles', 'from the Agent IO library'], ['Budget', '3 rounds · 6 roles · 20k tokens'], ['Can commit', 'no']] },
  each: { title: 'Swarm per workstream', rows: [['Block', 'Call pipeline'], ['Callee', 'Workstream swarm (core)'], ['Mode', 'each · up to 4 items'], ['Depth cap', 'nqube.maxCallDepth = 4']] },
  risky: { title: 'Any HIGH risk?', rows: [['Block', 'Branch filter'], ['Mode', 'keyword · "RISK: HIGH"'], ['Ports', 'pass → human · fail → merge']] },
  review: { title: 'Launch lead triages risk', rows: [['Queue', 'Review · urgent'], ['Why', 'a workstream graded itself HIGH'], ['Reject', 'drops the whole plan']] },
  merge: { title: 'Assemble + exec-ready', rows: [['Blocks', 'LLM transform → Refine loop'], ['Goal', 'decision in the first five lines'], ['Rounds', 'draft → critique → revise · ≤2']] },
  signoff: { title: 'Exec sign-off', rows: [['Queue', 'Review · high'], ['Gates', 'two side effects at once']] },
  pub: { title: 'Publish launch plan', rows: [['Capability', 'knowledge · launch'], ['Audience', 'internal']] },
  announce: { title: 'Announce', rows: [['Capability', 'webhook · announce'], ['Integration', 'admin-owned · sandbox'], ['Secret', 'held by the runtime']] },
};
const NQ_SELECT = ['plan', 'each', 'risky', 'review', 'merge', 'signoff', 'announce', 'announce'];

export function NqubeStage() {
  const tick = useStageTick(1500, 8);
  const selected = NQ_SELECT[Math.min(tick, NQ_SELECT.length - 1)];
  const insp = NQ_INSPECT[selected];
  const subStage = tick < 1 ? -1 : tick === 1 ? 1 : 3;
  const activeEdges = NQ_EDGES.filter((e) => e.stage === tick);
  return (
    <div className="st-app st-nq">
      <div className="st-toolbar">
        <strong>Example: Product launch — swarm of swarms</strong>
        <span className="nq-run-badge"><i className={`nq-light nq-light-${tick >= 7 ? 'done' : 'running'}`} /> {tick >= 7 ? 'completed' : 'running'}</span>
        <span className="st-chip">batch</span>
        <span className="st-chip">11 blocks · 2 swarms deep</span>
        <span className="st-spacer" />
        <span className="st-chip">agentOS ✓</span>
      </div>
      <div className="st-nq-body">
        <div className="st-canvas st-canvas-wide st-span3">
          <div className="st-canvas-inner">
            <svg className="st-wires" aria-hidden="true">
              {NQ_EDGES.map((e) => (
                <path key={e.id} d={e.d} className={`st-wire ${e.stage <= tick ? '' : 'is-idle'} ${e.dim && tick >= 3 ? 'is-skipped' : ''}`} />
              ))}
              {activeEdges.map((e) => (
                <circle key={`p-${e.id}`} r="4" className="nq-packet"><animateMotion dur="1.5s" repeatCount="indefinite" path={e.d} /></circle>
              ))}
              {/* callee bracket under "Swarm per workstream" */}
              <path d={`M${byId.each.x + NQ_W / 2} ${byId.each.y + 72} L ${byId.each.x + NQ_W / 2} ${SUB_Y - 14}`} className={`st-wire st-wire-call ${tick === 1 ? '' : 'is-idle'}`} />
            </svg>
            {NQ_EDGES.filter((e) => e.label).map((e) => (
              <span key={`l-${e.id}`} className={`nq-port-label ${e.dim && tick >= 3 ? 'is-skipped' : ''}`} style={{ left: e.lp.x, top: e.lp.y }}>{e.label}</span>
            ))}
            {NQ_NODES.map((n) => {
              const st = nodeState(n.stage, tick);
              const badge = n.badge ? n.badge(tick) : null;
              return (
                <div
                  key={n.id}
                  className={`nq-node ${st === 'running' ? 'nq-running' : st === 'done' ? 'nq-done' : 'nq-queued'} ${selected === n.id ? 'nq-selected' : ''}`}
                  style={{ left: n.x, top: n.y, width: NQ_W }}
                >
                  <div className="nq-node-head">
                    <span className="nq-cat-dot" style={{ background: n.color }} />
                    <span className="nq-node-title">{n.title}</span>
                    <i className={`nq-light nq-light-${st}`} />
                  </div>
                  <div className="nq-node-type">{n.type}</div>
                  <div className="nq-node-status">{st}</div>
                  {badge && <div className="nq-node-preview">{badge}</div>}
                </div>
              );
            })}
            <div className={`st-callee ${tick === 1 ? 'is-live' : tick > 1 ? 'is-done' : ''}`} style={{ left: nx(0), top: SUB_Y - 30, width: SUB_PITCH * 3 + 16 }}>
              <span className="st-callee-title">↳ Workstream swarm (core) · runs once per item · {tick === 1 ? 'call 3 of 4' : tick > 1 ? '4 calls done' : 'queued'}</span>
              {NQ_SUB.map((s, i) => {
                const st = subStage < 0 ? 'queued' : subStage === i ? 'running' : subStage > i ? 'done' : 'queued';
                return (
                  <div key={s.id} className={`nq-node nq-node-sm ${st === 'running' ? 'nq-running' : st === 'done' ? 'nq-done' : 'nq-queued'}`} style={{ left: SUB_X0 - nx(0) + i * SUB_PITCH, top: 24, width: 144 }}>
                    <div className="nq-node-head">
                      <span className="nq-cat-dot" style={{ background: s.color }} />
                      <span className="nq-node-title">{s.title}</span>
                      <i className={`nq-light nq-light-${st}`} />
                    </div>
                    <div className="nq-node-type">{s.type}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <aside className="st-inspector">
          <div className="st-side-title">{insp.title}</div>
          {insp.rows.map(([k, v]) => (
            <div key={k} className="st-kv">
              <div className="st-field">{k}</div>
              <div className="st-value">{v}</div>
            </div>
          ))}
          <p className="st-note">Coordinators and loops propose. A keyword or a person gates. Only the runtime commits — and only to a declared capability.</p>
        </aside>
      </div>
      <div className="nq-legend">
        <span><i className="nq-light nq-light-queued" /> queued</span>
        <span><i className="nq-light nq-light-running" /> processing</span>
        <span><i className="nq-light nq-light-done" /> done</span>
        <span><i className="nq-light nq-light-skipped" /> skipped branch</span>
        <span className="nq-legend-note">Seeded as an Example in every org · Duplicate it, swap the brief, run.</span>
      </div>
    </div>
  );
}

const ATH_SERIES = Array.from({ length: 18 }, (_, i) => ({
  x: i,
  y: 18 + Math.round(12 * Math.sin(i / 2.4) + (i % 4) * 2),
}));
const ATH_ERRS = ATH_SERIES.map((p) => ({ x: p.x, y: Math.max(0, Math.round(p.y * 0.06)) }));
const ATH_GRID = Array.from({ length: 7 }, (_, d) =>
  Array.from({ length: 24 }, (_, h) => (h >= 8 && h <= 18 && d < 5 ? 4 + ((d + h) % 5) : (h % 7 === 0 ? 1 : 0))));

const ATH_ROWS = [
  { id: 'r1', run: 'invoice-intake #4112', kind: 'pipeline', status: 'done', dur: '2m 14s', started: '12:41' },
  { id: 'r2', run: 'invoice-intake #4111', kind: 'pipeline', status: 'running', dur: '—', started: '12:39' },
  { id: 'r3', run: 'support-intake', kind: 'agent', status: 'done', dur: '48s', started: '12:31' },
  { id: 'r4', run: 'invoice-intake #4110', kind: 'pipeline', status: 'error', dur: '1m 02s', started: '12:18' },
];

export function AthenaStage() {
  const tick = useStageTick(2200, ATH_ROWS.length);
  return (
    <div className="st-app st-ath">
      <div className="st-toolbar">
        <strong>Athena · Insights</strong>
        <span className="st-chip on">7 days</span>
        <span className="st-chip">30 days</span>
        <span className="st-spacer" />
        <span className="st-chip">sample</span>
      </div>
      <div className="st-kpis">
        <StatCard label="Active runs" value={6} spark={[3, 4, 5, 4, 6]} color={COLORS.teal} />
        <StatCard label="Completed · 7 days" value={1284} spark={[8, 11, 9, 14, 12]} color={COLORS.green} />
        <StatCard label="Error rate" display="1.8%" value={2} color={COLORS.amber} />
        <StatCard label="Review queue" value={4} spark={[8, 6, 7, 5, 4]} color={COLORS.violet} />
      </div>
      <div className="st-ath-grid">
        <div className="st-panel st-span2">
          <div className="st-side-title">Runs over time</div>
          <AreaChart height={150} series={[{ name: 'completed', color: COLORS.teal, points: ATH_SERIES }, { name: 'errored', color: COLORS.red, points: ATH_ERRS }]} formatX={() => ''} />
        </div>
        <div className="st-panel">
          <div className="st-side-title">Outcomes</div>
          <Donut size={132} thickness={14} centerLabel="runs" slices={[{ label: 'done', value: 86, color: COLORS.green }, { label: 'gated', value: 9, color: COLORS.amber }, { label: 'error', value: 5, color: COLORS.red }]} />
        </div>
        <div className="st-panel st-span3">
          <div className="st-side-title">When the work lands</div>
          <Heatmap grid={ATH_GRID} cell={11} gap={2} />
        </div>
      </div>
      <DataTable
        rows={ATH_ROWS}
        rowKey={(r) => r.id}
        columns={[
          { key: 'run', label: 'Run', render: (r) => <strong className={r.id === ATH_ROWS[tick].id ? 'st-row-on' : ''}>{r.run}</strong> },
          { key: 'kind', label: 'Kind' },
          { key: 'status', label: 'Status' },
          { key: 'dur', label: 'Duration', align: 'right' },
          { key: 'started', label: 'Started', align: 'right' },
        ]}
      />
    </div>
  );
}

const LISTEN_FILES = [
  ['INV-20841.pdf', '184 KB', 'Sep 26 09:14'],
  ['INV-20840.pdf', '96 KB', 'Sep 26 09:02'],
  ['manifest.json', '2 KB', 'Sep 26 08:40'],
];

export function ListenStage() {
  const tick = useStageTick(1600, LISTEN_FILES.length);
  const file = LISTEN_FILES[tick];
  return (
    <div className="st-app">
      <div className="st-split">
      <div className="st-panel">
        <div className="st-side-title">Datasets · AP workspace</div>
        <div className="st-card on">
          <strong>ap-inbox</strong>
          <span>412 files · mounted read-only</span>
        </div>
        <div className="st-card">
          <strong>vendor-master</strong>
          <span>live connection · synced</span>
        </div>
        <div className="st-side-title">Files</div>
        {LISTEN_FILES.map(([name, size, when], i) => (
          <div key={name} className={`st-file ${i === tick ? 'on' : ''}`}>
            <span>{name}</span>
            <em>{size}</em>
            <em>{when}</em>
          </div>
        ))}
      </div>
      <div className="st-panel">
        <div className="st-side-title">Preview · {file[0]}</div>
        <pre className="st-preview">{`Vendor   Acme Industrial
Invoice  INV-20841
Amount   $12,480.00
Due      Oct 14, 2026

The invoice pipeline reads this dataset by reference.
The key that lists the bucket is not in the block.`}</pre>
        <p className="st-note">Pipelines, workloads, and dev spaces mount this source. They do not get a copy of the credential.</p>
      </div>
      </div>
    </div>
  );
}

const CO_LOGS = [
  'status requested → launching',
  'task invoice-extract started',
  'mounted dataset ap-inbox read-only',
  'read INV-20841.pdf',
  'extracted vendor=Acme amount=12480',
  'amount > 10000 · paused for review',
  'waiting on Review · not posted',
];

export function DevOpsStage() {
  const tick = useStageTick(1100, CO_LOGS.length);
  return (
    <div className="st-app">
      <div className="st-split">
      <div className="st-panel">
        <div className="st-side-title">Workloads · AP workspace</div>
        <div className="st-card">
          <i className="nq-light nq-light-running" />
          <strong>invoice-extract</strong>
          <span>{tick >= CO_LOGS.length - 1 ? 'running · gated' : 'running'}</span>
        </div>
        <div className="st-card">
          <i className="nq-light nq-light-queued" />
          <strong>nightly-embed</strong>
          <span>launching</span>
        </div>
        <div className="st-card">
          <i className="nq-light nq-light-error" />
          <strong>vendor-sync</strong>
          <span>error</span>
          <div className="st-btnrow">
            <span className="st-btn">Logs</span>
            <span className="st-btn">Stop</span>
          </div>
        </div>
        <p className="st-note">Scripts run on managed compute where the plan includes it. Team watches pipelines. It does not include this fleet.</p>
      </div>
      <div className="st-panel st-term">
        <div className="st-side-title">invoice-extract · log</div>
        <pre className="st-log">{CO_LOGS.map((line, i) => (i === tick ? `▸ ${line}` : `  ${line}`)).join('\n')}</pre>
      </div>
      </div>
    </div>
  );
}

const DIFF_FILES = [
  {
    id: 'gate',
    path: 'src/intake/gate.js',
    hunks: [
      {
        header: '@@ -1,10 +1,20 @@',
        lines: [
          ['ctx', '1', '1', "import { postInvoice } from './post.js';"],
          ['ctx', '2', '2', ''],
          ['add', '', '3', '/** Hold above this. Under it, post. The kit checkout is not imported here. */'],
          ['add', '', '4', 'export const HOLD_OVER = 10_000;'],
          ['ctx', '3', '5', ''],
          ['del', '4', '', 'export async function decide(job) {'],
          ['add', '', '6', 'export function decide(job) {'],
          ['del', '5', '', '  const amount = job.fields.amount;'],
          ['add', '', '7', '  const amount = Number(job.fields?.amount);'],
          ['ctx', '6', '8', '  if (!Number.isFinite(amount) || amount <= 0) {'],
          ['ctx', '7', '9', "    return { action: 'reject', reason: 'missing amount' };"],
          ['ctx', '8', '10', '  }'],
          ['add', '', '11', '  if (amount > HOLD_OVER) {'],
          ['add', '', '12', '    return {'],
          ['add', '', '13', "      action: 'hold',"],
          ['add', '', '14', "      reason: 'holdForApproval',"],
          ['add', '', '15', '      amount,'],
          ['add', '', '16', "      queue: 'ap-inbox',"],
          ['add', '', '17', '    };'],
          ['add', '', '18', '  }'],
          ['ctx', '9', '19', '  return postInvoice({ ...job, amount });'],
          ['ctx', '10', '20', '}'],
        ],
      },
    ],
  },
  {
    id: 'test',
    path: 'src/intake/gate.test.js',
    hunks: [
      {
        header: '@@ -0,0 +1,16 @@',
        lines: [
          ['add', '', '1', "import { decide, HOLD_OVER } from './gate.js';"],
          ['add', '', '2', ''],
          ['add', '', '3', "test('holds invoices over the threshold', () => {"],
          ['add', '', '4', "  const job = { id: 'INV-20841', fields: { amount: 12480 } };"],
          ['add', '', '5', '  expect(decide(job)).toEqual({'],
          ['add', '', '6', "    action: 'hold',"],
          ['add', '', '7', "    reason: 'holdForApproval',"],
          ['add', '', '8', '    amount: 12480,'],
          ['add', '', '9', "    queue: 'ap-inbox',"],
          ['add', '', '10', '  });'],
          ['add', '', '11', '});'],
          ['add', '', '12', ''],
          ['add', '', '13', "test('posts an invoice sitting on the threshold', () => {"],
          ['add', '', '14', '  const job = { id: \'INV-20840\', fields: { amount: HOLD_OVER } };'],
          ['add', '', '15', "  expect(decide(job).action).toBe('post');"],
          ['add', '', '16', '});'],
        ],
      },
    ],
  },
  {
    id: 'post',
    path: 'src/intake/post.js',
    hunks: null,
    lines: [
      ['ctx', '1', '1', 'export async function postInvoice(job) {'],
      ['ctx', '2', '2', '  return erp.post(job.fields);'],
      ['ctx', '3', '3', '}'],
    ],
  },
];

function diffTally(file) {
  let add = 0;
  let del = 0;
  (file.hunks || []).forEach((h) => h.lines.forEach(([kind]) => {
    if (kind === 'add') add += 1;
    if (kind === 'del') del += 1;
  }));
  return { add, del };
}

const DEV_TABS = [
  ['chat', 'Chat'],
  ['plan', 'Plan'],
  ['diff', 'Diff'],
  ['preview', 'Preview'],
  ['map', 'Map'],
  ['health', 'Health'],
  ['deploy', 'PR & Deploy'],
];

const MAP_NODES = [
  { id: 'host', name: 'Hosting', tier: 'prod · UAT', detail: 'Both sites answer. The preview channel is a third URL, and it expires.' },
  { id: 'api', name: 'API', tier: 'prod', detail: 'The request you are looking at already came through the API. That is the health check.' },
  { id: 'workers', name: 'Workers', tier: 'service', detail: 'The API service. A backend deploy rolls these tasks. Desired count is how many stay up.', scale: true },
  { id: 'runners', name: 'Runners', tier: 'jobs', detail: 'Deploy runners and job runners. Fan-out work and Merge & Deploy both land here.', scale: true },
  { id: 'gpu', name: 'GPU', tier: 'scale to zero', detail: 'Idle until a pipeline block asks for the GPU class. The task starts, runs, and stops. You are not renting a card that sits on.', scale: true },
  { id: 'db', name: 'Database', tier: 'data', detail: 'The console reads live config from the database. Saves are audited.' },
  { id: 'store', name: 'Storage', tier: 'data', detail: 'Raw uploads and public media, checked as their own buckets.' },
  { id: 'mail', name: 'Mail', tier: 'data', detail: 'Sending is its own check. Sandbox and production are different states.' },
];

const MAP_EXTRA = {
  'host-uat': { name: 'Hosting · UAT', detail: 'The landing zone. Merge & Deploy arrives here before anyone promotes to production.' },
  preview: { name: 'Preview channel', detail: 'A third URL for this pull request. It expires after 7 days. It is not production.' },
};

const BEATS = ['request in', 'worker claim', 'db write', 'runner job'];

function ServiceMap({ fleet, sel, onSelect, compact, onFleet, onHealth }) {
  const tick = useStageTick(900, BEATS.length);
  const beat = BEATS[tick];
  const workerOn = fleet.workers ? tick % fleet.workers : -1;
  const runnerOn = fleet.runners ? tick % fleet.runners : -1;
  const gpuOn = fleet.gpu ? tick % fleet.gpu : -1;
  const picked = MAP_EXTRA[sel] || MAP_NODES.find((n) => n.id === sel);
  const Lane = ({ id, title, count, on, empty, hot }) => (
    <div className={`st-lane ${sel === id ? 'on' : ''}`}>
      <button type="button" className="st-lane-head" onClick={() => onSelect(id)}>
        <strong>{title}</strong>
        <em>{count ? `${count} up` : empty}</em>
      </button>
      <div className="st-slots">
        {count > 0 ? Array.from({ length: count }, (_, i) => (
          <span key={i} className={`st-slot ${on === i ? (hot ? 'hot' : 'busy') : ''}`}>
            <i />
            {on === i ? (hot ? 'running' : id === 'runners' ? 'job' : 'claim') : 'steady'}
          </span>
        )) : <span className="st-slot idle">{empty}</span>}
      </div>
    </div>
  );
  return (
    <div className={`st-arch ${compact ? 'compact' : ''}`}>
      <div className="st-arch-label">Edge</div>
      <div className="st-edge">
        {[
          ['host', 'Hosting', 'prod'],
          ['host-uat', 'Hosting', 'UAT'],
          ['preview', 'Preview', 'expires 7d'],
        ].map(([id, name, tier]) => (
          <button type="button" key={id} className={`st-node ok ${sel === id ? 'on' : ''} ${id === 'preview' ? 'ghost' : ''}`} onClick={() => onSelect(id)}>
            <i />
            <strong>{name}</strong>
            <em>{tier}</em>
          </button>
        ))}
      </div>
      <div className="st-rail" aria-hidden="true"><i className="st-be-packet" /></div>
      <div className="st-be">
        <div className="st-be-trace">
          <span>INV-20841 · {beat}</span>
          <div className="st-be-track" aria-hidden="true"><i className="st-be-packet" /></div>
        </div>
        <div className="st-arch-label">Backend</div>
        <button type="button" className={`st-node ok st-be-api ${sel === 'api' ? 'on' : ''} ${beat === 'request in' ? 'pulse' : ''}`} onClick={() => onSelect('api')}>
          <i />
          <strong>API</strong>
          <em>{beat}</em>
        </button>
        <div className="st-rail" aria-hidden="true"><i className="st-be-packet" /></div>
        <div className="st-be-grid">
          <Lane id="workers" title="Workers" count={fleet.workers} on={workerOn} empty="none desired" />
          <Lane id="runners" title="Runners" count={fleet.runners} on={runnerOn} empty="none desired" />
          <Lane id="gpu" title="GPU" count={fleet.gpu} on={gpuOn} empty="scale to zero" hot />
        </div>
      </div>
      <div className="st-rail" aria-hidden="true"><i className="st-be-packet slow" /></div>
      <div className="st-arch-label">Data</div>
      <div className="st-data">
        {['db', 'store', 'mail'].map((id) => {
          const n = MAP_NODES.find((m) => m.id === id);
          return (
            <button type="button" key={id} className={`st-node ok ${sel === id ? 'on' : ''} ${id === 'db' && beat === 'db write' ? 'pulse' : ''}`} onClick={() => onSelect(id)}>
              <i />
              <strong>{n.name}</strong>
              <em>{n.tier}</em>
            </button>
          );
        })}
      </div>
      {!compact && picked && (
        <div className="st-map-detail">
          <strong>{picked.name}</strong>
          <p>{picked.detail}</p>
          {picked.scale && (
            <div className="st-scale">
              <button type="button" className="st-btn" onClick={() => onFleet(sel, -1)} aria-label={`Fewer ${picked.name}`}>−</button>
              <span>{fleet[sel]} desired</span>
              <button type="button" className="st-btn" onClick={() => onFleet(sel, 1)} aria-label={`More ${picked.name}`}>+</button>
              <button type="button" className="st-btn" onClick={onHealth}>Health</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChatSketch({ onMap }) {
  return (
    <ServiceMap
      compact
      fleet={{ workers: 2, runners: 1, gpu: 0 }}
      sel=""
      onSelect={onMap}
    />
  );
}

function DevChat({ onMap }) {
  const [sent, setSent] = useState(false);
  return (
    <div className="st-dev-view">
      <div className="st-side-title">Chat · layer repo only</div>
      <div className="st-chat">
        <div className="st-bubble you">
          <b>You</b>
          Hold invoices over $10,000 before they post. Do not touch the kit. Show me the instance while you do it.
        </div>
        <div className="st-bubble">
          <b>Agent</b>
          Planning against <code>src/intake</code> only. The kit checkout is read-only. The hold goes in <code>gate.js</code>, with a test beside it. This is the instance I checked before touching a file. Nothing deploys until you click Merge &amp; Deploy.
          <ChatSketch onMap={onMap} />
          <button type="button" className="st-btn" onClick={onMap}>Open the map</button>
        </div>
        {sent && (
          <div className="st-bubble you">
            <b>You</b>
            On a Kit instance this opens a real session. This page is the preview of that console.
          </div>
        )}
      </div>
      <form className="st-composer" onSubmit={(e) => { e.preventDefault(); setSent(true); }}>
        <input aria-label="Ask the agent" placeholder="Ask for a change in the layer repo…" />
        <button type="submit" className="st-btn primary">Send</button>
      </form>
    </div>
  );
}

function DevPlan({ onExecute }) {
  return (
    <div className="st-dev-view">
      <div className="st-side-title">Plan · not executed yet</div>
      <ol className="st-dev-plan">
        <li>Read <code>src/intake/gate.js</code> on the layer.</li>
        <li>When amount is over $10,000, return <code>holdForApproval</code> instead of posting.</li>
        <li>Leave the under-threshold path on <code>erp.post</code>.</li>
        <li>Open a pull request. Kit paths stay git-excluded.</li>
      </ol>
      <div className="st-files">
        <div className="st-file on">src/intake/gate.js <em>will change</em></div>
        <div className="st-file">src/intake/post.js <em>context only</em></div>
      </div>
      <div className="st-btnrow">
        <button type="button" className="st-btn primary" onClick={onExecute}>Approve &amp; execute</button>
        <span className="st-muted">Execution uses the session model. You still review the diff.</span>
      </div>
    </div>
  );
}

function DevDiff() {
  const [fileId, setFileId] = useState('gate');
  const file = DIFF_FILES.find((f) => f.id === fileId);
  const tally = diffTally(file);
  const rows = file.hunks
    ? file.hunks.flatMap((h) => [['hunk', '', '', h.header], ...h.lines])
    : file.lines;
  return (
    <div className="st-dev-view">
      <div className="st-side-title">
        {file.path}
        <em>{file.hunks ? `+${tally.add} −${tally.del}` : 'not in this pull request'}</em>
      </div>
      <div className="st-diff-layout">
        <div className="st-files">
          {DIFF_FILES.map((f) => {
            const t = diffTally(f);
            return (
              <button type="button" key={f.id} className={`st-file ${fileId === f.id ? 'on' : ''}`} onClick={() => setFileId(f.id)}>
                <span>{f.path.replace('src/intake/', '')}</span>
                <em>{f.hunks ? `+${t.add} −${t.del}` : 'unchanged'}</em>
              </button>
            );
          })}
        </div>
        <pre className="st-diff">
          {rows.map(([kind, oldNo, newNo, text], i) => (
            <div key={i} className={`st-diff-line ${kind}`}>
              {kind === 'hunk' ? text : (
                <>
                  <span className="st-gutter">{oldNo}</span>
                  <span className="st-gutter">{newNo}</span>
                  <span className="st-diff-mark">{kind === 'add' ? '+' : kind === 'del' ? '−' : ' '}</span>
                  <span>{text || ' '}</span>
                </>
              )}
            </div>
          ))}
        </pre>
      </div>
      {fileId === 'post' && <p className="st-note">Post stays a runtime call. The hold belongs in the gate, and the test covers both sides of the threshold.</p>}
    </div>
  );
}

function DevPreview() {
  const [amount, setAmount] = useState('12480');
  const held = Number(amount) > 10000;
  return (
    <div className="st-dev-view">
      <div className="st-url">preview · acme /invoices/new <span>branch, not production</span></div>
      <div className="st-miniapp">
        <strong>New invoice</strong>
        <label>Vendor <input value="Acme Industrial" readOnly /></label>
        <label>Invoice <input value="INV-20841" readOnly /></label>
        <label>
          Amount
          <input
            aria-label="Invoice amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          />
        </label>
        <div className={held ? 'st-hold' : 'st-ok'}>
          {held ? 'Held for approval · over $10,000' : 'Under the threshold · would post'}
        </div>
      </div>
    </div>
  );
}

const DEPLOY_LOG = {
  uat: [
    'merged layer/gate-10k into main',
    'frontend build · hosting release',
    'UAT deploy: done · production untouched',
  ],
  prod: [
    'promote the same main',
    'frontend release to production',
    'production deploy: done',
  ],
};

function DevDeploy({ illustrateLive }) {
  const [env, setEnv] = useState('uat');
  const [targets, setTargets] = useState({ frontend: true, backend: false, rules: false });
  const [landed, setLanded] = useState(illustrateLive ? 'uat' : null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const go = () => {
    if (busy || !Object.values(targets).some(Boolean)) return;
    setBusy(true);
    timer.current = window.setTimeout(() => {
      setLanded(env);
      setBusy(false);
    }, 700);
  };
  const liveHere = landed === env;
  return (
    <div className="st-dev-view">
      <div className="st-side-title">
        layer/gate-10k
        <span className={`st-chip ${liveHere ? 'on' : ''}`}>
          {busy ? 'deploying' : liveHere ? `${env === 'uat' ? 'UAT' : 'Production'} live` : 'not deployed'}
        </span>
      </div>
      <div className="st-dev-env">
        {['uat', 'prod'].map((id) => (
          <button type="button" key={id} className={env === id ? 'on' : ''} onClick={() => setEnv(id)}>
            {id === 'uat' ? 'UAT' : 'Production'}
          </button>
        ))}
      </div>
      <div className="st-dev-targets">
        {[
          ['frontend', 'Frontend'],
          ['backend', 'Backend · fast overlay'],
          ['rules', 'Rules'],
        ].map(([id, label]) => (
          <button
            type="button"
            key={id}
            className={targets[id] ? 'on' : ''}
            onClick={() => setTargets((t) => ({ ...t, [id]: !t[id] }))}
          >
            {label}
          </button>
        ))}
      </div>
      <pre className="st-log st-deploy-log">
        {(liveHere ? DEPLOY_LOG[env] : ['waiting for Merge & Deploy', 'UAT is the landing zone', 'production is a promote']).map((line) => `  ${line}`).join('\n')}
      </pre>
      <div className="st-btnrow">
        <button type="button" className="st-btn primary" onClick={go} disabled={busy}>
          {env === 'prod' ? 'Promote to production' : liveHere ? 'Redeploy UAT' : 'Merge & Deploy'}
        </button>
        <span className="st-muted">Frontend, backend, and rules ship separately. The kit image stays read-only.</span>
      </div>
    </div>
  );
}

function DevMap({ fleet, onFleet, onHealth }) {
  const [sel, setSel] = useState('workers');
  return (
    <div className="st-dev-view">
      <div className="st-side-title">Architecture · checked live</div>
      <ServiceMap fleet={fleet} sel={sel} onSelect={setSel} onFleet={onFleet} onHealth={onHealth} />
    </div>
  );
}

function DevHealth({ fleet, onFleet }) {
  const [group, setGroup] = useState('workers');
  const [focus, setFocus] = useState('workers');
  const picked = MAP_NODES.find((n) => n.id === focus);
  const rows = MAP_NODES.map((n) => ({
    ...n,
    line: n.scale
      ? `${fleet[n.id]} running${n.id === 'gpu' && !fleet[n.id] ? ' · idle' : ''}`
      : 'ok',
  }));
  const logs = group === 'workers'
    ? ['api task steady', 'worker claimed invoice batch', 'rollout completed']
    : ['runner started deploy job', fleet.gpu ? 'gpu class requested · launching' : 'gpu pool idle · scale to zero', 'job runner idle'];
  return (
    <div className="st-dev-view">
      <div className="st-side-title">Service health · every check</div>
      <div className="st-health">
        {rows.map((n) => (
          <button type="button" key={n.id} className={`st-file ${focus === n.id ? 'on' : ''}`} onClick={() => setFocus(n.id)}>
            <i className={`st-dot ${n.id === 'gpu' && !fleet.gpu ? 'idle' : 'ok'}`} />
            <strong>{n.name}</strong>
            <em>{n.line}</em>
          </button>
        ))}
      </div>
      {picked?.scale && (
        <div className="st-scale">
          <button type="button" className="st-btn" onClick={() => onFleet(focus, -1)} aria-label={`Fewer ${picked.name}`}>−</button>
          <span>{fleet[focus]} {picked.name.toLowerCase()}</span>
          <button type="button" className="st-btn" onClick={() => onFleet(focus, 1)} aria-label={`More ${picked.name}`}>+</button>
        </div>
      )}
      <div className="st-dev-env">
        <button type="button" className={group === 'workers' ? 'on' : ''} onClick={() => setGroup('workers')}>API + workers</button>
        <button type="button" className={group === 'runners' ? 'on' : ''} onClick={() => setGroup('runners')}>Runners</button>
      </div>
      <pre className="st-log st-deploy-log">{logs.map((line) => `  ${line}`).join('\n')}</pre>
      <p className="st-note">Workers and runners are services this console deploys and watches. A GPU task starts when a pipeline block asks for that class, then stops. + and − only move this picture. This page does not launch a machine.</p>
    </div>
  );
}

function DevOpsPanel({ title, rows }) {
  return (
    <div className="st-dev-view">
      <div className="st-side-title">{title}</div>
      {rows.map(([k, v]) => (
        <div key={k} className="st-file"><strong>{k}</strong><em>{v}</em></div>
      ))}
    </div>
  );
}

export function DevConsoleStage() {
  const [locked, setLocked] = useState(null);
  const [fleet, setFleet] = useState({ workers: 2, runners: 1, gpu: 0 });
  const tick = useStageTick(2400, DEV_TABS.length, locked !== null);
  const view = locked || DEV_TABS[tick][0];
  const onTab = ['chat', 'plan', 'diff', 'preview', 'map', 'health', 'deploy'].includes(view);
  const pick = (id) => setLocked(id);
  const onFleet = (id, delta) => setFleet((f) => ({ ...f, [id]: Math.max(0, Math.min(8, (f[id] || 0) + delta)) }));
  return (
    <div className="st-app st-dev">
      <div className="st-dev-shell">
        <aside className="st-panel st-dev-side">
          <button type="button" className="st-btn primary" onClick={() => pick('chat')}>+ New session</button>
          <div className="st-dev-views">
            {[['config', 'Config'], ['access', 'Access'], ['health', 'Ops']].map(([id, label]) => (
              <button type="button" key={id} className={view === id ? 'on' : ''} onClick={() => pick(id)}>{label}</button>
            ))}
          </div>
          <button type="button" className={`st-card ${onTab ? 'on' : ''}`} onClick={() => pick('chat')}>
            <strong>Gate the $10k hold</strong>
            <span>{view === 'deploy' ? 'deploy' : 'PR'}</span>
          </button>
          <button type="button" className={`st-card ${view === 'mount' ? 'on' : ''}`} onClick={() => pick('mount')}>
            <strong>Mount ap-inbox</strong>
            <span>done</span>
          </button>
          <button type="button" className={`st-card ${view === 'copy' ? 'on' : ''}`} onClick={() => pick('copy')}>
            <strong>Invoice form copy</strong>
            <span>done</span>
          </button>
        </aside>
        <div className="st-dev-main">
          <div className="st-dev-tabs" role="tablist">
            {DEV_TABS.map(([id, label]) => (
              <button
                type="button"
                key={id}
                role="tab"
                aria-selected={view === id}
                className={view === id ? 'on' : ''}
                onClick={() => pick(id)}
              >
                {label}
              </button>
            ))}
            {locked && (
              <button type="button" className="st-dev-tour" onClick={() => setLocked(null)}>Play the tour</button>
            )}
          </div>
          <div className="st-panel st-dev-stage">
            {view === 'chat' && <DevChat onMap={() => pick('map')} />}
            {view === 'plan' && <DevPlan onExecute={() => pick('diff')} />}
            {view === 'diff' && <DevDiff />}
            {view === 'preview' && <DevPreview />}
            {view === 'map' && <DevMap fleet={fleet} onFleet={onFleet} onHealth={() => pick('health')} />}
            {view === 'health' && <DevHealth fleet={fleet} onFleet={onFleet} />}
            {view === 'deploy' && <DevDeploy illustrateLive={!locked} />}
            {view === 'config' && (
              <DevOpsPanel
                title="Config · audited"
                rows={[
                  ['Preview channels', 'on · expire after 7 days'],
                  ['Who can deploy', 'builders'],
                  ['Agent scope', 'layer repo only'],
                ]}
              />
            )}
            {view === 'access' && (
              <DevOpsPanel
                title="Access · this instance"
                rows={[
                  ['you', 'admin'],
                  ['alex', 'builder'],
                  ['sam', 'viewer · not a seat'],
                ]}
              />
            )}
            {view === 'mount' && (
              <DevOpsPanel
                title="Mount ap-inbox · done"
                rows={[
                  ['Source', 'Listen dataset ap-inbox'],
                  ['Mount', 'read-only in the workspace'],
                  ['Credential', 'stayed on Listen'],
                ]}
              />
            )}
            {view === 'copy' && (
              <DevOpsPanel
                title="Invoice form copy · done"
                rows={[
                  ['Change', 'label copy on the intake form'],
                  ['Preview', 'checked'],
                  ['Deploy', 'not requested'],
                ]}
              />
            )}
          </div>
        </div>
      </div>
      <p className="st-note">Click the map, a service, or + to add workers, runners, or a GPU task. The tour plays until you do. On an instance those counts are real. Here they only change the picture.</p>
    </div>
  );
}

// App Builder, after ship: the lifecycle rail (Lab · Ready to ship ·
// Operate), the status strip the instance feeds, the app on two of its
// surfaces, and the Operate column — app map + "Needs attention".
// Static; the capture target for art/modules/app-builder.png (/_stages).
const BUILDER_TABS = ['Lab', 'Ready to ship', 'Operate'];
export function AppBuilderStage() {
  const [tab, setTab] = useState('Operate');
  return (
    <div className="st-app st-dev st-builder">
      <div className="st-dev-shell">
        <aside className="st-panel st-dev-side">
          <div className="st-side-title">Supplier portal <span className="st-chip on">live</span></div>
          <div className="st-builder-strip">
            {[['health', 'healthy · 99.98%', 'ok'], ['costs', '$41.80 mtd · $98 projected', 'ok'], ['reviews', '1 waiting', 'warn'], ['release', 'uat ahead by 1', 'ok']].map(([k, v, tone]) => (
              <div key={k} className={`st-builder-light is-${tone}`}><i /><span>{k}</span><em>{v}</em></div>
            ))}
          </div>
          <div className="st-side-title">Surfaces</div>
          <div className="st-files">
            <div className="st-file on">web · suppliers.acme.apps</div>
            <div className="st-file">desktop · macOS · Windows</div>
            <div className="st-file">phone · iOS · Android</div>
          </div>
          <div className="st-side-title">Agents</div>
          <div className="st-files">
            <div className="st-file">Supplier desk · public</div>
            <div className="st-file">Onboarding · members</div>
          </div>
        </aside>
        <div className="st-dev-main">
          <div className="st-dev-tabs" role="tablist">
            {BUILDER_TABS.map((t) => (
              <button type="button" key={t} role="tab" aria-selected={tab === t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>{t}</button>
            ))}
            <span className="st-dev-tour">v2.4 · layer-acme-suppliers · prod c41e9a2</span>
          </div>
          <div className="st-panel st-dev-stage st-builder-stage">
            <div className="st-builder-preview">
              <div className="st-url">suppliers.acme.apps <span>web · live</span></div>
              <div className="st-miniapp">
                <strong>Supplier onboarding · step 3 of 4</strong>
                <label>Company <input value="Nordic Fasteners AB" readOnly /></label>
                <label>Tax id <input value="SE556… · verified" readOnly /></label>
                <label>Bank <input value="IBAN · pending document" readOnly /></label>
                <div className="st-hold">Held · a buyer approves the bank change</div>
              </div>
              <div className="st-builder-phone">
                <div className="st-url">phone <span>same layer</span></div>
                <div className="st-miniapp">
                  <strong>Deliveries</strong>
                  <span style={{ width: 'auto' }}>DN-5520 · scanned 09:12 · matched PO-7731</span>
                  <span style={{ width: 'auto' }}>DN-5519 · awaiting weight ticket</span>
                  <div className="st-ok">offline sync on</div>
                </div>
              </div>
            </div>
            <div className="st-builder-operate">
              <div className="st-side-title">Needs attention</div>
              <div className="st-files">
                <div className="st-file on">1 review waiting · bank change · Nordic Fasteners</div>
                <div className="st-file">UAT twin is 1 release ahead · promote to prod</div>
                <div className="st-file">TLS renews in 71 days · nothing to do</div>
              </div>
              <div className="st-side-title">App map</div>
              <div className="st-files">
                <div className="st-file">edge → api → workers → db · all running</div>
                <div className="st-file">capabilities · erp.read_po · mail.send · webhook.announce</div>
                <div className="st-file">knowledge · 3 docs · supplier handbook pending</div>
              </div>
              <div className="st-side-title">Last 24 h</div>
              <div className="st-files">
                <div className="st-file">48 suppliers signed in · 212 commits · 0 retries fired twice</div>
                <div className="st-file">prod deploy done · health-gated · 5 h ago</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p className="st-note">The studio owns the app after ship: the strip and the Operate tab read the instance; Lab is the workbench for the next change; Ready to ship promotes the UAT twin.</p>
    </div>
  );
}

// The Human Review item. Defaults are the invoice example; the homepage spine
// passes the launch example's risk triage (same card, other queue item).
export function ReviewCard({
  decided,
  title = 'Review',
  meta = 'INV-20841 · $12,480 · over $10,000',
  preview = 'vendor: Acme Industrial\ninvoice: INV-20841\namount: 12480.00\ndue: 2026-10-14',
  reject = 'Reject',
  extra = null,
  note = 'Edit the fields, then approve. Reject stops the branch.',
}) {
  return (
    <div className="st-review">
      <div className="st-review-head">
        <strong>{title}</strong>
        <span>{meta}</span>
      </div>
      <pre className="st-preview">{preview}</pre>
      <div className="st-btnrow">
        <span className={`st-btn primary ${decided ? 'on' : ''}`}>{decided ? 'Approved' : 'Approve'}</span>
        <span className="st-btn">{reject}</span>
        {extra && <span className="st-btn">{extra}</span>}
        <span className="st-muted">{note}</span>
      </div>
    </div>
  );
}
